import React from 'react';
import {
  Box,
  Button,
  Checkbox,
  Flex,
  Input,
  NumberInput,
  NumberInputField,
  Radio,
  Select,
  Text,
  Textarea,
  useToast,
} from '@chakra-ui/react';
import { BiTrash, BiPlus, BiTimeFive, BiX } from 'react-icons/bi';

import { createInteraction, formatMs, INTERACTION_TYPE_OPTIONS } from './interactionsSpec';

/**
 * Editor visual de las interacciones de un video interactivo.
 * Soporta quiz_mcq, voice_open y voice_pronunciation.
 */
export default function InteractionsEditor({ source, interactions, setInteractions }) {
  const videoRef = React.useRef(null);
  const toasts = useToast();
  const [newType, setNewType] = React.useState('quiz_mcq');

  const getCurrentMs = () => Math.round((videoRef.current?.currentTime || 0) * 1000);

  const addInteraction = () => {
    const anchorMs = getCurrentMs();
    if (interactions.some((it) => it.anchorMs === anchorMs)) {
      return toasts({
        title: 'Ya existe una interacción en ese punto del video',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
    }
    setInteractions([...interactions, createInteraction(newType, anchorMs)].sort((a, b) => a.anchorMs - b.anchorMs));
  };

  const updateInteraction = (id, patch) => {
    setInteractions(interactions.map((it) => (it.id === id ? { ...it, ...patch } : it)));
  };

  const removeInteraction = (id) => {
    const r = window.confirm('¿Estás seguro(a) de eliminar esta interacción?');
    if (r) setInteractions(interactions.filter((it) => it.id !== id));
  };

  const setAnchorToCurrent = (id) => {
    updateInteraction(id, { anchorMs: getCurrentMs() });
    setInteractions((prev) => [...prev].sort((a, b) => a.anchorMs - b.anchorMs));
  };

  const changeType = (interaction, type) => {
    if (type === interaction.type) return;
    const next = createInteraction(type, interaction.anchorMs);
    next.id = interaction.id;
    next.required = interaction.required;
    next.retry = interaction.retry;
    setInteractions(interactions.map((it) => (it.id === interaction.id ? next : it)));
  };

  const updateOption = (interaction, optionId, patch) => {
    updateInteraction(interaction.id, {
      options: interaction.options.map((o) => (o.id === optionId ? { ...o, ...patch } : o)),
    });
  };

  const setCorrectOption = (interaction, optionId) => {
    updateInteraction(interaction.id, {
      options: interaction.options.map((o) => ({ ...o, correct: o.id === optionId })),
    });
  };

  const addOption = (interaction) => {
    const nextId = String.fromCharCode(97 + interaction.options.length);
    updateInteraction(interaction.id, {
      options: [...interaction.options, { id: nextId, text: '', correct: false }],
    });
  };

  const removeOption = (interaction, optionId) => {
    if (interaction.options.length <= 2) {
      return toasts({
        title: 'Una pregunta debe tener al menos 2 opciones',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
    }
    updateInteraction(interaction.id, {
      options: interaction.options.filter((o) => o.id !== optionId),
    });
  };

  const updateConcept = (interaction, conceptId, label) => {
    updateInteraction(interaction.id, {
      expectedConcepts: interaction.expectedConcepts.map((c) => (c.id === conceptId ? { ...c, label } : c)),
    });
  };

  const addConcept = (interaction) => {
    const id = `c${Date.now()}`;
    updateInteraction(interaction.id, {
      expectedConcepts: [...(interaction.expectedConcepts || []), { id, label: '' }],
    });
  };

  const removeConcept = (interaction, conceptId) => {
    const list = interaction.expectedConcepts || [];
    if (list.length <= 1) {
      return toasts({
        title: 'Debe quedar al menos un concepto esperado',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
    }
    updateInteraction(interaction.id, {
      expectedConcepts: list.filter((c) => c.id !== conceptId),
    });
  };

  const typeLabel = (type) => INTERACTION_TYPE_OPTIONS.find((o) => o.value === type)?.label || type;

  return (
    <Box>
      <Box marginBottom="10px">
        <video ref={videoRef} src={source} controls style={{ width: '100%', maxHeight: '340px', background: '#000' }} />
      </Box>

      <Flex gap="8px" alignItems="center" flexWrap="wrap">
        <Select size="sm" maxWidth="280px" value={newType} onChange={(e) => setNewType(e.target.value)}>
          {INTERACTION_TYPE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </Select>
        <Button
          leftIcon={<BiPlus />}
          colorScheme="#574feb"
          bg="#574feb"
          color="white"
          size="sm"
          onClick={addInteraction}
        >
          Agregar en el tiempo actual
        </Button>
      </Flex>

      {interactions.length === 0 && (
        <Text marginTop="15px" fontSize="14px" color="#727d90">
          Aún no hay interacciones. Reproduce el video, páusalo en el punto deseado y agrega una pregunta o checkpoint
          de voz. Mientras no haya interacciones, el estudiante verá el video de forma tradicional.
        </Text>
      )}

      {interactions.map((interaction, index) => (
        <Box
          key={interaction.id}
          marginTop="15px"
          padding="15px"
          border="1px solid"
          borderColor="gray.200"
          borderRadius="8px"
        >
          <Flex justifyContent="space-between" alignItems="center">
            <Flex alignItems="center" gap="8px" flexWrap="wrap">
              <Text fontWeight="600" color="#574feb">
                {index + 1} — {formatMs(interaction.anchorMs)}
              </Text>
              <Button size="xs" leftIcon={<BiTimeFive />} onClick={() => setAnchorToCurrent(interaction.id)}>
                Usar tiempo actual
              </Button>
            </Flex>
            <Button size="xs" colorScheme="red" variant="ghost" onClick={() => removeInteraction(interaction.id)}>
              <BiTrash />
            </Button>
          </Flex>

          <Box marginTop="10px">
            <Box fontSize="14px">Tipo</Box>
            <Select
              size="sm"
              maxWidth="320px"
              value={interaction.type || 'quiz_mcq'}
              onChange={(e) => changeType(interaction, e.target.value)}
            >
              {INTERACTION_TYPE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Select>
            <Text fontSize="12px" color="#727d90" marginTop="4px">
              {typeLabel(interaction.type)}
            </Text>
          </Box>

          {interaction.type === 'quiz_mcq' && (
            <>
              <Box marginTop="10px">
                <Box fontSize="14px">Pregunta</Box>
                <Textarea
                  value={interaction.question}
                  rows={2}
                  onChange={(e) => updateInteraction(interaction.id, { question: e.target.value })}
                />
              </Box>

              <Box marginTop="10px" fontSize="14px">
                Opciones (marca la correcta)
              </Box>
              {(interaction.options || []).map((option) => (
                <Flex key={option.id} marginTop="8px" alignItems="center" gap="8px">
                  <Radio isChecked={!!option.correct} onChange={() => setCorrectOption(interaction, option.id)} />
                  <Input
                    size="sm"
                    flex="1"
                    placeholder="Texto de la opción"
                    value={option.text}
                    onChange={(e) => updateOption(interaction, option.id, { text: e.target.value })}
                  />
                  <Button
                    size="sm"
                    variant="ghost"
                    color="gray.600"
                    fontSize="20px"
                    aria-label="Eliminar opción"
                    onClick={() => removeOption(interaction, option.id)}
                  >
                    <BiX />
                  </Button>
                </Flex>
              ))}
              <Button size="xs" marginTop="8px" leftIcon={<BiPlus />} onClick={() => addOption(interaction)}>
                Agregar opción
              </Button>
            </>
          )}

          {interaction.type === 'voice_open' && (
            <>
              <Box marginTop="10px">
                <Box fontSize="14px">Pregunta (el estudiante responde hablando)</Box>
                <Textarea
                  value={interaction.question || ''}
                  rows={2}
                  placeholder="Ej: Explica qué es el núcleo celular y cuál es su función principal"
                  onChange={(e) => updateInteraction(interaction.id, { question: e.target.value })}
                />
              </Box>

              <Box marginTop="10px" fontSize="14px">
                Conceptos esperados
              </Box>
              {(interaction.expectedConcepts || []).map((concept) => (
                <Flex key={concept.id} marginTop="8px" alignItems="center" gap="8px">
                  <Input
                    size="sm"
                    flex="1"
                    placeholder="Ej: Contiene el ADN"
                    value={concept.label}
                    onChange={(e) => updateConcept(interaction, concept.id, e.target.value)}
                  />
                  <Button
                    size="sm"
                    variant="ghost"
                    aria-label="Eliminar concepto"
                    onClick={() => removeConcept(interaction, concept.id)}
                  >
                    <BiX />
                  </Button>
                </Flex>
              ))}
              <Button size="xs" marginTop="8px" leftIcon={<BiPlus />} onClick={() => addConcept(interaction)}>
                Agregar concepto
              </Button>

              <Flex marginTop="12px" gap="16px" flexWrap="wrap">
                <Box>
                  <Text fontSize="13px" mb="4px">
                    Duración máx. (seg)
                  </Text>
                  <NumberInput
                    size="sm"
                    maxWidth="90px"
                    min={10}
                    max={120}
                    value={interaction.maxSeconds ?? 60}
                    onChange={(_, value) =>
                      updateInteraction(interaction.id, {
                        maxSeconds: Number.isNaN(value) ? 60 : value,
                      })
                    }
                  >
                    <NumberInputField />
                  </NumberInput>
                </Box>
                <Box>
                  <Text fontSize="13px" mb="4px">
                    Umbral de aprobación
                  </Text>
                  <NumberInput
                    size="sm"
                    maxWidth="90px"
                    min={0}
                    max={100}
                    value={interaction.passThreshold ?? 60}
                    onChange={(_, value) =>
                      updateInteraction(interaction.id, {
                        passThreshold: Number.isNaN(value) ? 60 : value,
                      })
                    }
                  >
                    <NumberInputField />
                  </NumberInput>
                </Box>
              </Flex>
            </>
          )}

          {interaction.type === 'voice_pronunciation' && (
            <>
              <Box marginTop="10px">
                <Box fontSize="14px">Frase a pronunciar</Box>
                <Textarea
                  value={interaction.referenceText || ''}
                  rows={2}
                  placeholder="Ej: What is your name?"
                  onChange={(e) =>
                    updateInteraction(interaction.id, {
                      referenceText: e.target.value,
                      question: e.target.value,
                    })
                  }
                />
              </Box>
              <Flex marginTop="12px" gap="16px" flexWrap="wrap" alignItems="flex-end">
                <Box>
                  <Text fontSize="13px" mb="4px">
                    Duración máx. (seg)
                  </Text>
                  <NumberInput
                    size="sm"
                    maxWidth="90px"
                    min={5}
                    max={60}
                    value={interaction.maxSeconds ?? 30}
                    onChange={(_, value) =>
                      updateInteraction(interaction.id, {
                        maxSeconds: Number.isNaN(value) ? 30 : value,
                      })
                    }
                  >
                    <NumberInputField />
                  </NumberInput>
                </Box>
                <Box>
                  <Text fontSize="13px" mb="4px">
                    Umbral de aprobación
                  </Text>
                  <NumberInput
                    size="sm"
                    maxWidth="90px"
                    min={0}
                    max={100}
                    value={interaction.passThreshold ?? 70}
                    onChange={(_, value) =>
                      updateInteraction(interaction.id, {
                        passThreshold: Number.isNaN(value) ? 70 : value,
                      })
                    }
                  >
                    <NumberInputField />
                  </NumberInput>
                </Box>
                <Box flex="1" minWidth="200px">
                  <Text fontSize="13px" mb="4px">
                    Pronunciación figurada (opcional)
                  </Text>
                  <Input
                    size="sm"
                    placeholder="uát is yor néim"
                    value={interaction.figurative || ''}
                    onChange={(e) =>
                      updateInteraction(interaction.id, {
                        figurative: e.target.value,
                      })
                    }
                  />
                </Box>
              </Flex>
            </>
          )}

          <Flex marginTop="16px" alignItems="center" gap="20px">
            <Checkbox
              isChecked={!!interaction.required}
              onChange={(e) => updateInteraction(interaction.id, { required: e.target.checked })}
            >
              <Text fontSize="14px">Obligatoria para continuar</Text>
            </Checkbox>
            <Flex alignItems="center" gap="8px">
              <Text fontSize="14px">Intentos:</Text>
              <NumberInput
                size="sm"
                maxWidth="70px"
                min={1}
                max={5}
                value={interaction.retry?.maxAttempts ?? 2}
                onChange={(_, value) =>
                  updateInteraction(interaction.id, {
                    retry: { ...interaction.retry, maxAttempts: Number.isNaN(value) ? 1 : value },
                  })
                }
              >
                <NumberInputField />
              </NumberInput>
            </Flex>
          </Flex>
        </Box>
      ))}
    </Box>
  );
}
